<template>
  <header class="navbar-wrap">
    <div class="navbar" :class="{ 'navbar-anonymous': !authStore.isAuthenticated }">
      <div class="brand-controls">
        <router-link to="/" class="brand">
          <img
            src="@/assets/inredbox.png"
            alt="INREDBOX"
            class="brand-logo-img"
          />
        </router-link>

        <button type="button" class="hamburger" @click="mobileMenuOpen = !mobileMenuOpen"
          aria-label="Меню" :aria-expanded="mobileMenuOpen" aria-controls="main-navigation">
          <i :class="mobileMenuOpen ? 'pi pi-times' : 'pi pi-bars'"></i>
        </button>
      </div>

      <nav id="main-navigation" class="links" :class="{ open: mobileMenuOpen }" aria-label="Основная навигация">
        <router-link to="/" class="nav-link">
          <i class="pi pi-home"></i>
          <span>Главная</span>
        </router-link>

        <router-link v-if="authStore.isAuthenticated" to="/products" class="nav-link">
          <i class="pi pi-th-large"></i>
          <span>Каталог</span>
        </router-link>

        <router-link
          v-if="authStore.isAuthenticated"
          to="/orders"
          class="nav-link"
        >
          <i class="pi pi-history"></i>
          <span>История заказов</span>
        </router-link>

        <router-link v-if="authStore.isAuthenticated" to="/cart" class="nav-link cart-link">
          <i class="pi pi-shopping-bag"></i>
          <span>Проекты</span>
          <span v-if="cartStore.totalQuantity > 0" class="cart-badge">
            {{ cartStore.totalQuantity }}
          </span>
        </router-link>

        <router-link v-if="authStore.isAuthenticated" to="/cabinet" class="nav-link">
          <i class="pi pi-user"></i>
          <span>Кабинет</span>
        </router-link>

        <router-link v-if="showAdminPanel" to="/admin" class="nav-link">
          <i class="pi pi-shield"></i>
          <span>Управление</span>
        </router-link>

        <router-link
          v-if="!authStore.isAuthenticated"
          to="/auth"
          class="nav-link login-link"
        >
          <i class="pi pi-sign-in"></i>
          <span>Войти</span>
        </router-link>

        <NotificationDropdown v-if="canSeeNotifications" />

        <span v-if="authStore.isAuthenticated" class="user">
          {{ authStore.user?.fullName }}
        </span>

        <button
          v-if="authStore.isAuthenticated"
          class="logout"
          @click="handleLogout"
        >
          <i class="pi pi-sign-out"></i>
          <span>Выйти</span>
        </button>
      </nav>
    </div>

    <Dialog
      v-model:visible="showCreateCartDialog"
      header="Создание проекта"
      :modal="true"
      :style="{ width: '400px' }"
    >
      <div class="create-cart-form">
        <div class="form-field">
          <label for="cartName">Название проекта</label>
          <InputText
            id="cartName"
            v-model="newCartName"
            placeholder="Введите название"
            autofocus
            @keyup.enter="confirmCreateCart"
          />
        </div>
        <div class="dialog-footer">
          <Button label="Отмена" text @click="showCreateCartDialog = false" />
          <Button
            label="Создать"
            severity="primary"
            :disabled="!newCartName.trim()"
            :loading="isCreating"
            @click="confirmCreateCart"
          />
        </div>
      </div>
    </Dialog>
  </header>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Button from "primevue/button";

import NotificationDropdown from "@/modules/notifications/components/NotificationDropdown.vue";

import { useAuthStore } from "@/modules/auth/store/auth.store";
import { useCartStore } from "@/modules/cart/store/cart.store";
import { useNotificationStore } from "@/modules/notifications/store/notification.store";
import { usePermissionsStore } from "@/modules/auth/store/permissions.store";
import { logoutRequest } from "@/modules/auth/api/auth.service";
import { publishAuthChanged } from "@/modules/auth/services/auth-channel.service";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const cartStore = useCartStore();
const permissionsStore = usePermissionsStore();

const mobileMenuOpen = ref(false);
const showCreateCartDialog = ref(false);
const newCartName = ref("");
const isCreating = ref(false);
let interval = null;

const userRoles = computed(() => {
  if (!authStore.isAuthenticated) return [];
  return authStore.user?.roles ?? [];
});

const canSeeNotifications = computed(() => {
  return (
    authStore.isAuthenticated &&
    permissionsStore.hasAnyPermission([
      "ReceiveOrderNotifications",
      "ReceiveNewUserNotifications",
      "ReviewRegistrations",
      "EditNotifications",
    ])
  );
});

async function loadCart() {
  try {
    await cartStore.loadUserCarts();
  } catch (error) {
    console.error("Ошибка загрузки корзины", error);
  }
}

async function safeLoadNotifications() {
  try {
    await notificationStore.fetchNotifications();
  } catch (error) {
    console.error("Ошибка загрузки уведомлений", error);
  }
}

function startNotificationsPolling() {
  stopNotificationsPolling();
  interval = setInterval(async () => {
    if (canSeeNotifications.value) {
      await safeLoadNotifications();
    }
  }, 30000);
}

function stopNotificationsPolling() {
  if (interval) {
    clearInterval(interval);
    interval = null;
  }
}

async function confirmCreateCart() {
  if (!newCartName.value.trim() || isCreating.value) return;

  isCreating.value = true;
  try {
    await cartStore.createCart(newCartName.value.trim());
    showCreateCartDialog.value = false;
    newCartName.value = "";
    await cartStore.loadUserCarts();
  } catch (error) {
    console.error("Ошибка при создании проекта", error);
  } finally {
    isCreating.value = false;
  }
}

function clearNotificationsState() {
  if ("notifications" in notificationStore) {
    notificationStore.notifications = [];
  }
  if ("unreadCount" in notificationStore) {
    notificationStore.unreadCount = 0;
  }
}

watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated) => {
    if (!isAuthenticated) {
      cartStore.clearLocalState();
      return;
    }
    await loadCart();
    if (canSeeNotifications.value) {
      await safeLoadNotifications();
      startNotificationsPolling();
    } else {
      stopNotificationsPolling();
      clearNotificationsState();
    }
  },
  { immediate: true },
);

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false;
});

onUnmounted(() => {
  stopNotificationsPolling();
});

async function handleLogout() {
  try {
    await logoutRequest();
    publishAuthChanged();
  } catch (error) {
    console.error("Ошибка выхода из аккаунта", error);
  }
  stopNotificationsPolling();
  clearNotificationsState();
  authStore.clearUser();
  permissionsStore.clearPermissions();
  cartStore.clearLocalState();
  router.push("/");
}

const showAdminPanel = computed(() => {
  if (!authStore.isAuthenticated) return false;

  if (
    userRoles.value.some((role) => ["Manager", "Admin", "God"].includes(role))
  ) {
    return true;
  }

  const adminPermissions = [
    "ViewUsers",
    "ReviewRegistrations",
    "ManageRoles",
    "ManagePermissions",
    "EditProducts",
    "ImportProductImages",
    "EditProductTypes",
    "EditProductAttributes",
    "EditBrand",
    "EditCategory",
    "ViewDiscount",
    "EditDiscount",
    "BlockUsers",
    "DeleteUsers",
    "EditCharacteristicValues",
    "EditVariantCharacteristics",
    "EditNotifications",
    "ChangeOrderStatus",
    "AssignOrder",
    "ReceiveOrderNotifications",
    "ManageHomePage",
    "ViewHomePage",
    "EditCarousel",
    "EditWeeklySelection",
    "ViewManagersStats",
  ];

  return permissionsStore.hasAnyPermission(adminPermissions);
});
</script>

<style scoped>
.navbar-wrap {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e9e9e9;
}

.navbar {
  max-width: 1240px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
}

@media (min-width: 1401px) {
  .navbar.navbar-anonymous {
    justify-content: flex-start;
  }
}

.brand-controls {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.brand {
  flex-shrink: 0;
}

.brand-logo-img {
  height: 40px;
  width: auto;
  display: block;
}

.links {
  display: flex;
  flex: 0 1 auto;
  gap: 10px;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: flex-start;
  min-width: 0;
}

.nav-link {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  white-space: nowrap;
  border-radius: 999px;
  color: #1d1d1d;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
}

.nav-link i {
  font-size: 14px;
  color: #666666;
  transition: color 0.2s ease;
}

.nav-link:hover {
  background: #f5f5f5;
  color: #111111;
}

.nav-link:hover i {
  color: #c41217;
}

.router-link-active.nav-link {
  background: #111111;
  color: #ffffff;
}

.router-link-active.nav-link i {
  color: #ffffff;
}

.login-link {
  border: 1px solid #dddddd;
}

.cart-link {
  position: relative;
}

.cart-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #c41217;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.user {
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 10px 14px;
  border-radius: 999px;
  background: #f5f5f5;
  color: #111111;
  font-size: 14px;
  font-weight: 600;
}

.logout {
  flex-shrink: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  padding: 10px 14px;
  border-radius: 999px;
  background: #111111;
  color: #ffffff;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.logout:hover {
  background: #2c2c2c;
}

 .cart-container {
    position: relative;
    display: inline-block;
  }

  .cart-dropdown {
    position: absolute;
    top: calc(100% - 5px);
    right: 0;
    width: 280px;
    background: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.12);
    z-index: 1002;
    margin-top: 10px;
  }

  .cart-projects-list {
    max-height: 250px;
    overflow-y: auto;
    margin: 10px 0;
  }

  .project-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
    border: 1px solid transparent;
    margin-bottom: 4px;
  }

  .project-item:hover {
    background: #f8f8f8;
  }

  .project-item.active-project {
    background: #fff5f5;
    border-color: #fbdada;
  }

  .project-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .project-name {
    font-size: 14px;
    font-weight: 600;
    color: #111111;
  }

  .project-details {
    font-size: 12px;
    color: #888888;
  }

  .active-project i {
    color: #c41217;
    font-size: 14px;
  }

  .dropdown-footer {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    gap: 8px;
    flex-direction: column;
  }

  .create-project-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    color: #c41217;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .create-project-footer:hover {
    background: #fff5f5;
    border-color: #c41217;
  }

  .cart-projects-list::-webkit-scrollbar {
    width: 4px;
  }

  .cart-projects-list::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 10px;
  }

  .project-name-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .manage-link {
    display: block;
    text-align: center;
    padding: 8px 12px;
    color: #c41217;
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
  }

  .manage-link:hover {
    background: #fff5f5;
    border-radius: 8px;
  }

  .create-cart-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-field label {
    font-weight: 600;
    color: #1d1d1d;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 8px;
  }

  .hamburger {
    display: none;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 999px;
    border: 1px solid #e1e1e1;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .hamburger i {
    font-size: 16px;
    color: #222222;
  }

  .hamburger:hover {
    background: #f6f6f6;
  }

  .hamburger:focus-visible {
    outline: 2px solid #c41217;
    outline-offset: 3px;
  }

  @media (max-width: 1400px) {
    .navbar {
      flex-direction: column;
      justify-content: flex-start;
      gap: 0;
      padding: 14px 16px;
      align-items: stretch;
    }

    .brand-controls {
      width: 100%;
      justify-content: space-between;
    }

    .hamburger {
      display: flex;
      margin-left: auto;
    }

    .links {
      display: none;
      flex: initial;
      justify-content: flex-start;
      flex-direction: column;
      align-items: stretch;
      width: 100%;
      gap: 4px;
      padding: 8px 0 12px;
      border-top: 1px solid #f0f0f0;
      margin-top: 8px;
    }

    .links.open {
      display: flex;
    }

    .nav-link {
      justify-content: flex-start;
      border-radius: 10px;
    }

    .cart-container {
      width: 100%;
    }

    .cart-link {
      width: 100%;
      border-radius: 10px;
    }

    .cart-dropdown {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      border-radius: 16px 16px 0 0;
      max-height: 80vh;
      overflow-y: auto;
      margin-top: 0;
    }

    .dropdown {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      border-radius: 16px 16px 0 0;
      max-height: 80vh;
      overflow-y: auto;
    }

    .user {
      width: 100%;
      max-width: none;
      text-align: center;
    }

    .logout {
      width: 100%;
      justify-content: center;
    }
  }

</style>
